import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import Spinner from "../../../ui/Spinner";
import ErrorBanner from "../../../ui/ErrorBanner";
import { logError } from "../../../utils/logger";
import {
  normalizeError,
  ValidationError,
  NetworkError,
  ServerError,
} from "../../../utils/apiErrors";

// Tab configuration
const TAB_CONFIG = {
  improvement: {
    id: "improvement",
    label: "Improvement",
    icon: LightBulbIcon,
    iconBgColor: "bg-primary",
    iconColor: "text-text-inverse",
    iconBgColorHover: "group-hover:bg-primary-100",
    title: "Suggest an Improvement",
    subtitle: "Help us make Traces better",
    categories: [
      { value: "feature", label: "New Feature" },
      { value: "enhancement", label: "Enhancement" },
      { value: "ui", label: "UI/UX Improvement" },
      { value: "performance", label: "Performance" },
      { value: "other", label: "Other" },
    ],
    defaultCategory: "feature",
    descriptionLabel: "Description",
    descriptionPlaceholder: "Describe your suggestion in detail...",
    descriptionHint: "Be as specific as possible about what you'd like to see",
    buttonText: "Submit Suggestion",
    buttonColor: "bg-primary hover:bg-primary-hover",
    emailHint: "We may follow up with you about your suggestion",
  },
  bug: {
    id: "bug",
    label: "Bug Report",
    icon: ExclamationTriangleIcon,
    iconBgColor: "bg-primary",
    iconColor: "text-text-inverse",
    iconBgColorHover: "group-hover:bg-primary-100",
    title: "Report a Bug",
    subtitle: "Help us fix what's broken",
    categories: [
      { value: "ui", label: "UI/Display Issue" },
      { value: "functionality", label: "Functionality Not Working" },
      { value: "performance", label: "Performance/Slow" },
      { value: "crash", label: "App Crash/Freeze" },
      { value: "data", label: "Data Not Saving" },
      { value: "other", label: "Other" },
    ],
    defaultCategory: "ui",
    descriptionLabel: "What happened?",
    descriptionPlaceholder: "Describe the bug and steps to reproduce it...",
    descriptionHint:
      "Include steps to reproduce, what you expected, and what actually happened",
    buttonText: "Submit Bug Report",
    buttonColor: "bg-primary hover:bg-primary-hover",
    emailHint: "We may contact you for more details about this bug",
  },
  support: {
    id: "support",
    label: "Support",
    icon: QuestionMarkCircleIcon,
    iconBgColor: "bg-primary",
    iconColor: "text-text-inverse",
    iconBgColorHover: "group-hover:bg-primary-100",
    title: "Get Help",
    subtitle: "We're here to assist you",
    categories: [
      { value: "general", label: "General Question" },
      { value: "account", label: "Account & Settings" },
      { value: "notes", label: "Notes & Locations" },
      { value: "privacy", label: "Privacy & Safety" },
      { value: "technical", label: "Technical Issue" },
      { value: "other", label: "Other" },
    ],
    defaultCategory: "general",
    descriptionLabel: "How can we help?",
    descriptionPlaceholder: "Describe what you need help with...",
    descriptionHint:
      "Provide as much detail as possible so we can help you quickly",
    buttonText: "Submit Request",
    buttonColor: "bg-primary hover:bg-primary-hover",
    emailHint: "We'll respond to this email address",
  },
};

export default function HelpFeedbackModal({
  open,
  onClose,
  onSubmit,
  userEmail,
  initialTab = "improvement",
}) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [category, setCategory] = useState(
    TAB_CONFIG[initialTab].defaultCategory,
  );
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Reset form when tab changes
  useEffect(() => {
    const config = TAB_CONFIG[activeTab];
    setCategory(config.defaultCategory);
    setDescription("");
    setSubmitError(null);
  }, [activeTab]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      setCategory(TAB_CONFIG[initialTab].defaultCategory);
      setDescription("");
      setSubmitError(null);
    }
  }, [open, initialTab]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!description.trim()) return;

    setSubmitError(null);
    setSubmitting(true);
    try {
      await onSubmit({ type: activeTab, category, description });
      // Reset form only on success
      setCategory(TAB_CONFIG[activeTab].defaultCategory);
      setDescription("");
      onClose();
    } catch (err) {
      logError("Failed to submit feedback:", err);
      const normalized = normalizeError(err);
      if (normalized instanceof NetworkError) {
        setSubmitError(
          "Unable to send feedback. Check your internet connection.",
        );
      } else if (normalized instanceof ValidationError) {
        setSubmitError(normalized.getUserMessage());
      } else if (normalized instanceof ServerError) {
        setSubmitError("Something went wrong on our side. Please try again.");
      } else {
        setSubmitError(
          normalized.message || "Failed to submit feedback. Please try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const config = TAB_CONFIG[activeTab];
  const Icon = config.icon;

  const modal = (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[70] p-4">
      <div className="bg-surface-elevated rounded-2xl shadow-2xl w-full max-w-[calc(100vw-2rem)] sm:max-w-lg relative flex flex-col max-h-[90vh] overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={submitting}
          className="absolute top-4 right-4 text-text-muted hover:text-text-secondary transition disabled:opacity-50 z-10"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        {/* Tabs */}
        <div className="flex border-b border-border px-6 pt-4">
          {Object.values(TAB_CONFIG).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              disabled={submitting}
              className={`
                flex-1 px-4 py-3 text-sm font-medium transition-all relative
                ${
                  activeTab === tab.id
                    ? "text-primary border-b-2 border-border-primary"
                    : "text-text-muted hover:text-text-secondary border-b-2 border-border-transparent"
                }
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {/* Header */}
          <div className="flex items-center gap-3 mt-6 mb-6 pb-4 border-b border-border">
            <div
              className={`w-12 h-12 rounded-full ${config.iconBgColor} flex items-center justify-center`}
            >
              <Icon className={`h-6 w-6 ${config.iconColor}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-text-primary">
                {config.title}
              </h2>
              <p className="text-sm text-text-muted">{config.subtitle}</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {submitError && (
              <ErrorBanner
                message={submitError}
                onDismiss={() => setSubmitError(null)}
                className="mb-4"
              />
            )}

            {/* Category */}
            <div className="mb-5">
              <label className="block text-sm font-medium mb-2 text-text-secondary">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setSubmitError(null);
                }}
                disabled={submitting}
                className="w-full bg-surface-elevated border border-border-strong rounded-lg px-4 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50 disabled:bg-surface-sunken"
              >
                {config.categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="mb-5">
              <label className="block text-sm font-medium mb-2 text-text-secondary">
                {config.descriptionLabel}{" "}
                <span className="text-destructive">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setSubmitError(null);
                }}
                placeholder={config.descriptionPlaceholder}
                required
                disabled={submitting}
                className="w-full bg-surface-elevated border border-border-strong rounded-lg px-4 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none disabled:opacity-50 disabled:bg-surface-sunken"
                rows="6"
              />
              <p className="text-xs text-text-muted mt-1">
                {config.descriptionHint}
              </p>
            </div>

            {/* Email (read-only) */}
            {userEmail && (
              <div className="mb-5">
                <label className="block text-sm font-medium mb-2 text-text-secondary">
                  Your Email
                </label>
                <input
                  type="email"
                  value={userEmail}
                  readOnly
                  className="w-full border border-border-strong rounded-lg px-4 py-2 text-text-secondary bg-surface focus:outline-none"
                />
                <p className="text-xs text-text-muted mt-1">
                  {config.emailHint}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-5 py-2 bg-surface-sunken text-text-secondary rounded-lg hover:bg-surface-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!description.trim() || submitting}
                className={`px-5 py-2 ${config.buttonColor} text-text-inverse rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
              >
                {submitting ? (
                  <>
                    <Spinner size={16} className="text-text-inverse" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  config.buttonText
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
